import type {
  ActivityEvent,
  IncidentSummary,
  ResponseOption,
  ScenarioEvidence,
  ServiceHealth,
  TraceNode,
} from "../domain";

export const campusSummary = {
  campusHealth: 92,
  activeIncidents: 3,
  assetsRequiringAttention: 18,
  maintenanceDueToday: 7,
};

export const serviceHealth: ServiceHealth[] = [
  {
    id: "svc-internet",
    name: "Internet Connectivity",
    status: "degraded",
    availability: 81.4,
    affectedBuildings: ["North Academic Center"],
    updatedAt: "10:31",
  },
  {
    id: "svc-voice",
    name: "Campus Voice",
    status: "healthy",
    availability: 99.9,
    affectedBuildings: [],
    updatedAt: "10:31",
  },
  {
    id: "svc-staff-wifi",
    name: "Staff Wireless",
    status: "disrupted",
    availability: 63.8,
    affectedBuildings: ["North Academic Center"],
    updatedAt: "10:30",
  },
  {
    id: "svc-student-wifi",
    name: "Student Wireless",
    status: "degraded",
    availability: 74.2,
    affectedBuildings: ["North Academic Center", "Cascade Library Annex"],
    updatedAt: "10:30",
  },
  {
    id: "svc-m365",
    name: "Microsoft 365",
    status: "healthy",
    availability: 99.8,
    affectedBuildings: [],
    updatedAt: "10:29",
  },
];

export const activeIncident: IncidentSummary = {
  id: "INC-2048",
  title: "North Academic Building Network Degradation",
  severity: "SEV-2",
  status: "investigating",
  owner: "Maya Chen",
  affectedUsers: 183,
  affectedServices: ["Staff Wireless", "Student Wireless", "VoIP registration"],
  affectedBuildings: ["North Academic Center"],
  workingTheory: "A recent trunk configuration change on CR-SW-04 overlaps with the first observed degradation.",
  latestUpdate: "Switch CR-SW-04 linked to the incident at 10:23.",
};

export const recentActivity: ActivityEvent[] = [
  { id: "act-1", actor: "Maya Chen", action: "acknowledged AP controller alerts", occurredAt: "10:18" },
  { id: "act-2", actor: "Jordan Ellis", action: "linked CR-SW-04 to INC-2048", occurredAt: "10:23" },
  { id: "act-3", actor: "Devin Brooks", action: "completed the Student Services phone review", occurredAt: "09:46" },
];

// Placeholder stubs for required demo anchors (to be replaced with fully typed entities in PKT-02)
export const demoAnchors = {
  changeRequest: "CHG-9012",
  dataJack: "NA-214-A1",
  mobilePhone: "MOB-0418",
  deskPhone: "TEL-0318",
  maintenanceTask: "MNT-882",
  assignment: "ASN-1182",
  vendor: "Cascade Fiber Cooperative"
};

export const traceNodes: TraceNode[] = [
  {
    id: "INC-2048",
    kind: "incident",
    eyebrow: "Incident",
    label: "North Academic degradation",
    detail: "183 people are experiencing unstable wireless and VoIP registration.",
    operationalDetail: "Correlated alerts began four minutes after change CHG-9012 completed.",
    architectureDetail: "Incident relationships resolve through stable IDs; no display labels are parsed.",
    status: "disrupted",
    evidence: "Incident correlation window",
  },
  {
    id: "svc-staff-wifi",
    kind: "service",
    eyebrow: "Service",
    label: "Staff Wireless",
    detail: "North Academic Center availability is below the campus service objective.",
    operationalDetail: "Availability fell to 63.8%; voice registration failures share the same building boundary.",
    architectureDetail: "Service impact is derived from the switch, VLAN, building, and endpoint relationships.",
    status: "disrupted",
    evidence: "Service health monitor",
  },
  {
    id: "CR-SW-04",
    kind: "switch",
    eyebrow: "Distribution switch",
    label: "CR-SW-04",
    detail: "The switch carries North Academic voice and staff wireless traffic.",
    operationalDetail: "CHG-9012 modified trunk allowance immediately before the first correlated alert.",
    architectureDetail: "Device → interface → VLAN traversal remains independent of the visual graph.",
    status: "degraded",
    evidence: "Switch configuration diff",
  },
  {
    id: "CR-SW-04:G1/0/18",
    kind: "switch-port",
    eyebrow: "Switch port",
    label: "Gi1/0/18",
    detail: "Active uplink serving patch-panel position NA-MDF-PP03/18.",
    operationalDetail: "Voice VLAN 120 is present; staff data tagging is inconsistent with intended state.",
    architectureDetail: "Interface record terminates the cable relationship to the patch-panel port.",
    status: "degraded",
    evidence: "Port telemetry and intended-state comparison",
  },
  {
    id: "NA-MDF-PP03:18",
    kind: "patch-panel",
    eyebrow: "Patch panel",
    label: "NA-MDF-PP03 / 18",
    detail: "Physical termination between the North Academic room run and CR-SW-04.",
    operationalDetail: "Continuity and label verification passed during the last closet inspection.",
    architectureDetail: "Patch-panel port provides the physical layer between switch port and wall jack.",
    status: "healthy",
    evidence: "Closet inspection MNT-861",
  },
  {
    id: "NA-214-A1",
    kind: "data-jack",
    eyebrow: "Wall jack",
    label: "NA-214-A1",
    detail: "Room 214, position A1, assigned to Academic Advising.",
    operationalDetail: "The jack resolves upstream to CR-SW-04 and downstream to desk phone TEL-0318.",
    architectureDetail: "Bidirectional traversal uses explicit termination IDs and assignment records.",
    status: "degraded",
    evidence: "Jack inventory and cable termination record",
  },
  {
    id: "VLAN-120",
    kind: "vlan",
    eyebrow: "Network segment",
    label: "VLAN 120 · Voice",
    detail: "Campus voice segment used by the assigned desk phone.",
    operationalDetail: "DHCP is healthy; registration traffic is intermittently lost at the trunk boundary.",
    architectureDetail: "VLAN membership is a relationship, not a property inferred from an IP address.",
    status: "degraded",
    evidence: "DHCP scope and call-manager registration",
  },
  {
    id: "TEL-0318",
    kind: "phone",
    eyebrow: "Desk phone",
    label: "TEL-0318",
    detail: "Cisco 8841 assigned through ASN-1182 in North Academic 214.",
    operationalDetail: "The phone remains powered but has failed two call-manager registrations.",
    architectureDetail: "Asset, assignment, jack, person, and service remain separately auditable records.",
    status: "offline",
    evidence: "Call-manager registration log",
  },
  {
    id: "USR-PRIYA-NAIR",
    kind: "person",
    eyebrow: "Assigned user",
    label: "Priya Nair",
    detail: "Academic Advising; primary user of TEL-0318.",
    operationalDetail: "Priya can use Teams calling as the documented temporary workaround.",
    architectureDetail: "Person impact is calculated from assignment and service relationships.",
    status: "degraded",
    evidence: "Assignment ASN-1182",
  },
];

export const scenarioEvidence: ScenarioEvidence[] = [
  {
    id: "EVD-2048-01",
    label: "CHG-9012 trunk diff",
    source: "Configuration change record",
    observedAt: "10:23",
    supports: "The timing and affected VLANs support rolling back the trunk change.",
    confidence: "high",
    traceNodeId: "CR-SW-04",
  },
  {
    id: "EVD-2048-02",
    label: "Gi1/0/18 error correlation",
    source: "Switch telemetry",
    observedAt: "10:26",
    supports: "Errors rise at the same time that voice registrations begin failing.",
    confidence: "high",
    traceNodeId: "CR-SW-04:G1/0/18",
  },
  {
    id: "EVD-2048-03",
    label: "TEL-0318 registration loss",
    source: "Call manager",
    observedAt: "10:28",
    supports: "The endpoint is powered but cannot maintain registration through VLAN 120.",
    confidence: "medium",
    traceNodeId: "TEL-0318",
  },
];

export const responseOptions: ResponseOption[] = [
  {
    id: "rollback",
    label: "Rollback CHG-9012",
    summary: "Restore the last verified trunk configuration and monitor registrations.",
    risk: "Brief reconvergence during rollback; lowest evidence gap.",
    result: "Services recover and affected phones re-register.",
    recoveryPercent: 100,
    remainingUsers: 0,
    minutesToRestore: 12,
    confidence: 94,
  },
  {
    id: "reroute",
    label: "Reroute affected traffic",
    summary: "Move traffic to the secondary distribution path while preserving the change.",
    risk: "Reduces impact, but leaves the suspected configuration in place.",
    result: "Most users recover; a smaller voice-registration group remains affected.",
    recoveryPercent: 88,
    remainingUsers: 36,
    minutesToRestore: 18,
    confidence: 82,
  },
  {
    id: "observe",
    label: "Observe for ten minutes",
    summary: "Collect more telemetry before changing the network.",
    risk: "Extends disruption despite two high-confidence correlated signals.",
    result: "Impact continues and the incident escalates, but the operator may revise the response.",
    recoveryPercent: 67,
    remainingUsers: 72,
    minutesToRestore: 27,
    confidence: 64,
  },
];
